import React, { useEffect, useRef, useState } from 'react';

const PAPER = '#F4F4F4';
const SILVER = '#CFCFCF';
const MIN_BITS = 2;
const MAX_BITS = 5;

// Hero graphic: an analog wave that gets sampled into digital steps.
// Pointer x moves the sampling point, pointer y changes the bit depth.
const SignalCanvas = ({ className = '', labels }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [bits, setBits] = useState(3);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let width = 0;
    let height = 0;
    let frame = 0;
    let visible = true;
    let pointerActive = false;
    let split = 0.45;
    let targetSplit = 0.45;
    let currentBits = 3;

    const signal = (x, t) => {
      const k = (2 * Math.PI) / Math.max(width, 600);
      return 0.62 * Math.sin(x * k * 2.2 + t) + 0.38 * Math.sin(x * k * 5.1 - t * 1.6);
    };

    const draw = (time) => {
      const t = reduceMotion ? 0 : time / 1400;
      if (reduceMotion) {
        split = targetSplit;
      } else {
        if (!pointerActive) targetSplit = 0.45 + 0.12 * Math.sin(time / 3200);
        split += (targetSplit - split) * 0.08;
      }

      ctx.clearRect(0, 0, width, height);
      const mid = height / 2;
      const amp = height * 0.36;
      const splitX = width * split;

      // Analog side
      ctx.lineWidth = 2.5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = PAPER;
      ctx.beginPath();
      ctx.moveTo(0, mid - signal(0, t) * amp);
      for (let x = 3; x < splitX; x += 3) {
        ctx.lineTo(x, mid - signal(x, t) * amp);
      }
      ctx.lineTo(splitX, mid - signal(splitX, t) * amp);
      ctx.stroke();

      // Digital side: sample, hold, quantize
      const step = width < 640 ? 18 : 28;
      const levels = 2 ** currentBits;
      const quantize = (v) => (Math.round(((v + 1) / 2) * (levels - 1)) / (levels - 1)) * 2 - 1;
      ctx.strokeStyle = SILVER;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineJoin = 'miter';
      ctx.beginPath();
      for (let x = splitX; x < width; x += step) {
        const y = mid - quantize(signal(x, t)) * amp;
        const end = Math.min(x + step, width);
        ctx.fillRect(x, Math.min(y, mid), end - x - 2, Math.abs(mid - y));
        if (x === splitX) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        ctx.lineTo(end, y);
      }
      ctx.stroke();

      // Sampling point
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(244, 244, 244, 0.5)';
      ctx.setLineDash([4, 6]);
      ctx.beginPath();
      ctx.moveTo(splitX, 0);
      ctx.lineTo(splitX, height);
      ctx.stroke();
      ctx.setLineDash([]);
    };

    const loop = (time) => {
      draw(time);
      if (visible) frame = requestAnimationFrame(loop);
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (reduceMotion) draw(0);
    };

    const handlePointerMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      pointerActive = true;
      targetSplit = Math.min(0.94, Math.max(0.06, (event.clientX - rect.left) / rect.width));
      const fromBottom = 1 - Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
      const nextBits = MIN_BITS + Math.round(fromBottom * (MAX_BITS - MIN_BITS));
      if (nextBits !== currentBits) {
        currentBits = nextBits;
        setBits(nextBits);
      }
      if (reduceMotion) draw(0);
    };

    const handlePointerLeave = () => {
      pointerActive = false;
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    const visibilityObserver = new IntersectionObserver(([entry]) => {
      const wasVisible = visible;
      visible = entry.isIntersecting;
      if (visible && !wasVisible && !reduceMotion) frame = requestAnimationFrame(loop);
    });
    visibilityObserver.observe(canvas);

    wrap.addEventListener('pointermove', handlePointerMove);
    wrap.addEventListener('pointerleave', handlePointerLeave);
    resize();
    if (!reduceMotion) frame = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
      wrap.removeEventListener('pointermove', handlePointerMove);
      wrap.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, []);

  return (
    <div ref={wrapRef} className={`relative touch-pan-y ${className}`}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={labels.aria}
        className="absolute inset-0 h-full w-full"
      />
      <div className="pointer-events-none absolute inset-x-5 top-0 flex justify-between text-sm md:inset-x-10" aria-hidden="true">
        <span>{labels.analog}</span>
        <span className="tabular-nums text-silver">
          {labels.sampled(bits, 2 ** bits)}
        </span>
      </div>
      <p className="pointer-events-none absolute inset-x-5 bottom-0 hidden text-sm text-paper/60 sm:block md:inset-x-10" aria-hidden="true">
        {labels.hint}
      </p>
    </div>
  );
};

export default SignalCanvas;
